import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDown, ArrowRightCircle, Search, Users} from 'lucide-react';
import { BLOGS, Blog } from '../components/data/blogs';
import { Link } from 'react-router-dom';

// Get the collective blog from BLOGS array
const GREH_PRAVESH_COLLECTIVE_BLOG = BLOGS.find(blog => blog.id === "greh-pravesh-collective-1");

const BlogsPage = () => {
  const [currentText, setCurrentText] = useState("Insights");
  console.log(setCurrentText)
  const textCycle = ["Insights", "Healing", "Growth", "Support"];
  console.log(textCycle);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [filteredBlogs, setFilteredBlogs] = useState(BLOGS);
 

  useEffect(() => {
    // Filter out the collective blog from regular filtering
    const regularBlogs = BLOGS.filter(blog => blog.id !== "greh-pravesh-collective-1");
    
    const filtered = regularBlogs.filter(blog => {
      const lowerSearchItem = searchTerm.toLowerCase();
      const matchesTitle = blog.title.toLowerCase().includes(lowerSearchItem);
      
      // Updated to handle author array - check if any author matches the search
      const matchesAuthor = blog.author && blog.author.length > 0 
        ? blog.author.some(author => author.name.toLowerCase().includes(lowerSearchItem))
        : false;

      const matchesSearch = matchesTitle || matchesAuthor; 
      const matchesTag = selectedTag ? blog.tags.includes(selectedTag) : true;

      return matchesSearch && matchesTag;
    });
    setFilteredBlogs(filtered);
  }, [searchTerm, selectedTag]);

  // Collective Blog Card Component - Special design for the collective blog
  const CollectiveBlogCard: React.FC<{ blog: Blog }> = ({ blog }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-xl overflow-hidden mb-12 transform hover:scale-[1.02] transition-all duration-300"
    >
      <div className="relative p-8">
        {/* Background pattern */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
          <Users size={128} className="text-white" />
        </div>
        
        {/* Collective badge */}
        <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
          <Users size={16} className="text-white mr-2" />
          <span className="text-white text-sm font-medium">Collective Article</span>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 leading-tight">
          {blog.title}
        </h2>
        
        <p className="text-white/90 mb-6 text-lg leading-relaxed">
          {blog.excerpt}
        </p>
        
        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-6">
          {blog.tags.slice(0, 4).map(tag => (
            <span key={tag} className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
              {tag}
            </span>
          ))}
        </div>
        
        <Link to={`/blog/${blog.slug}`}>
          <span className="inline-flex items-center text-white font-semibold text-lg hover:text-white/80 transition-colors group">
            Read Our Philosophy
            <ArrowRightCircle size={24} className="ml-2 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
      </div>
    </motion.div>
  );

  const BlogCard: React.FC<{ blog: Blog }> = ({ blog }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden"
    >
      {/* Replace featured image with a colored background and heading display */}
      <div className="h-48 overflow-hidden bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center p-6">
        <h3 className="text-xl font-semibold text-white text-center">{blog.title}</h3>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          {/* Updated to display multiple authors */}
          <div className="flex items-center text-gray-600">
            {blog.author && blog.author.length > 0 && (
              <>
                {/* Show first author's avatar */}
                <img
                  src={blog.author[0].avatar}
                  alt={blog.author[0].name}
                  className="w-8 h-8 rounded-full mr-3 object-cover"
                />
                {/* Show all authors' names */}
                <span>
                  {blog.author.map((author, index) => (
                    <span key={author.id}>
                      {author.name}
                      {index < blog.author.length - 1 && (
                        index === blog.author.length - 2 ? ' & ' : ', '
                      )}
                    </span>
                  ))}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {blog.tags.slice(0, 3).map(tag => (
            <span key={tag} className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm">
              {tag}
            </span>
          ))}
          {blog.tags.length > 3 && (
            <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm">
              +{blog.tags.length - 3}
            </span>
          )}
        </div>
        <p className="text-gray-600 mb-4 line-clamp-3">{blog.excerpt}</p>
        <Link to={`/blog/${blog.slug}`}>
          <span className="text-blue-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all">
            Read More <ArrowRightCircle size={20} />
          </span>
        </Link>
      </div>
    </motion.div>
  );

  return (
    <main className="bg-white">
      <section className="relative min-h-[70vh] flex flex-col overflow-hidden bg-gradient-to-br from-blue-600 to-blue-400">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-indigo-800 to-indigo-300 rounded-br-[100%] z-0 opacity-50" />

        <div className="container mx-auto px-4 pt-32 pb-16 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-block px-4 py-2 rounded-full bg-white/20"
            >
              <span className="text-sm font-medium">Expert Guidance & Resources</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-4xl md:text-5xl font-bold"
            >
              Mental Health & Wellness
            </motion.h1>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentText}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.6 }}
                className="text-3xl md:text-4xl font-light"
              >
                {currentText}
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="max-w-xl mx-auto mt-12"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles with titles or author name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <div className="text-center py-4 bg-white">
        <ArrowDown className="animate-bounce mx-auto text-blue-600" size={32} />
      </div>

      <section className="container mx-auto px-4 py-16">
        {/* Collective Blog Section - Always displayed at top */}
        {GREH_PRAVESH_COLLECTIVE_BLOG && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Philosophy</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 mx-auto rounded-full"></div>
            </div>
            <CollectiveBlogCard blog={GREH_PRAVESH_COLLECTIVE_BLOG} />
          </motion.div>
        )}

        {/* Author Blogs Section */}
        <div className="mb-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Insights from Our Therapists</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <button
              onClick={() => setSelectedTag("")}
              className={`px-4 py-2 rounded-full ${selectedTag === ""
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              All
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredBlogs.map((blog) => (
            <BlogCard key={blog.id} blog={blog} />
          ))}
        </div>
      </section>
    </main>
  );
};

export default BlogsPage;
 